import * as React from "react";
import { Check, ChevronDown, ChevronsUpDown, X, Search } from "lucide-react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as SheetPrimitive from "@radix-ui/react-dialog";

import { cn } from "../lib/utils";

function useIsTouch() {
  const [isTouch, setIsTouch] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(hover: none) or (pointer: coarse)");
    setIsTouch(mql.matches);

    const handler = (e) => setIsTouch(e.matches);
    if (mql.addEventListener) {
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    }
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, []);

  return isTouch;
}

function normalizeOptions(rawOptions) {
  if (!Array.isArray(rawOptions)) return [];
  return rawOptions.map((opt) => {
    if (typeof opt === "string" || typeof opt === "number") {
      return { value: String(opt), label: String(opt) };
    }
    if (opt && typeof opt === "object") {
      return {
        value: String(opt.value ?? opt.id ?? ""),
        label: String(opt.label ?? opt.name ?? opt.title ?? opt.value ?? ""),
        description: opt.description,
        badge: opt.badge,
        icon: opt.icon,
        disabled: Boolean(opt.disabled),
        raw: opt,
      };
    }
    return { value: "", label: "" };
  });
}

const Combobox = React.forwardRef(
  (
    {
      items,
      options: rawOptions,
      value: controlledValue,
      defaultValue,
      onChange,
      onValueChange,
      multiple = false,
      placeholder = "Select...",
      searchPlaceholder = "Search...",
      emptyText = "No items found.",
      disabled = false,
      maxChipsDisplay,
      className,
      align = "start",
      id: explicitId,
      ...props
    },
    ref
  ) => {
    const isTouch = useIsTouch();
    const generatedId = React.useId();
    const baseId = explicitId || generatedId;
    const listboxId = `combobox-${baseId}-listbox`;

    const [open, setOpen] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState("");
    const [highlightedIndex, setHighlightedIndex] = React.useState(0);
    const inputRef = React.useRef(null);
    const listRef = React.useRef(null);

    const options = React.useMemo(
      () => normalizeOptions(items || rawOptions),
      [items, rawOptions]
    );

    // Initial state handling
    const initialVal = React.useMemo(() => {
      if (defaultValue !== undefined) return defaultValue;
      return multiple ? [] : "";
    }, [defaultValue, multiple]);

    const [uncontrolledValue, setUncontrolledValue] = React.useState(initialVal);

    const isControlled = controlledValue !== undefined;
    const value = isControlled ? controlledValue : uncontrolledValue;

    const selectedValues = React.useMemo(() => {
      if (multiple) {
        return Array.isArray(value) ? value.map(String) : [];
      }
      return value !== undefined && value !== null && value !== "" ? [String(value)] : [];
    }, [value, multiple]);

    const emitChange = React.useCallback(
      (nextVal) => {
        if (!isControlled) {
          setUncontrolledValue(nextVal);
        }
        onChange?.(nextVal);
        onValueChange?.(nextVal);
      },
      [isControlled, onChange, onValueChange]
    );

    // Literal search matching (safe against regex chars like [, (, *)
    const filteredOptions = React.useMemo(() => {
      if (!searchQuery.trim()) return options;
      const lowerQuery = searchQuery.toLowerCase();
      return options.filter(
        (opt) =>
          opt.label.toLowerCase().includes(lowerQuery) ||
          (opt.description && opt.description.toLowerCase().includes(lowerQuery)) ||
          opt.value.toLowerCase().includes(lowerQuery)
      );
    }, [options, searchQuery]);

    // Reset highlighted index on filter changes
    React.useEffect(() => {
      setHighlightedIndex(0);
    }, [filteredOptions]);

    const getOptionId = React.useCallback(
      (optVal) => `${listboxId}-option-${String(optVal).replace(/[^a-zA-Z0-9]/g, "_")}`,
      [listboxId]
    );

    const activeDescendantId =
      open && filteredOptions[highlightedIndex]
        ? getOptionId(filteredOptions[highlightedIndex].value)
        : undefined;

    // Scroll active item into view
    React.useEffect(() => {
      if (open && filteredOptions[highlightedIndex]) {
        const optId = getOptionId(filteredOptions[highlightedIndex].value);
        const el = document.getElementById(optId);
        el?.scrollIntoView({ block: "nearest" });
      }
    }, [open, highlightedIndex, filteredOptions, getOptionId]);

    const handleSelectOption = React.useCallback(
      (opt) => {
        if (opt.disabled) return;

        if (multiple) {
          const isSelected = selectedValues.includes(opt.value);
          const nextValues = isSelected
            ? selectedValues.filter((v) => v !== opt.value)
            : [...selectedValues, opt.value];
          emitChange(nextValues);
          setSearchQuery("");
        } else {
          emitChange(opt.value);
          setSearchQuery("");
          setOpen(false);
        }
      },
      [multiple, selectedValues, emitChange]
    );

    const handleRemoveChip = React.useCallback(
      (valToRemove, e) => {
        e?.stopPropagation();
        if (disabled) return;
        const nextValues = selectedValues.filter((v) => v !== valToRemove);
        emitChange(nextValues);
      },
      [disabled, selectedValues, emitChange]
    );

    const handleKeyDown = (e) => {
      if (disabled) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (!open) {
          setOpen(true);
          return;
        }
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (!open) {
          setOpen(true);
          return;
        }
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
      } else if (e.key === "Enter") {
        if (open && filteredOptions[highlightedIndex]) {
          e.preventDefault();
          handleSelectOption(filteredOptions[highlightedIndex]);
        }
      } else if (e.key === "Escape") {
        setOpen(false);
      } else if (e.key === "Backspace" && multiple && !searchQuery) {
        // Backspace removes last chip when query is empty
        if (selectedValues.length > 0) {
          e.preventDefault();
          const lastVal = selectedValues[selectedValues.length - 1];
          handleRemoveChip(lastVal);
        }
      }
    };

    const selectedOptions = React.useMemo(() => {
      return selectedValues
        .map((v) => options.find((opt) => opt.value === v) || { value: v, label: v })
        .filter(Boolean);
    }, [selectedValues, options]);

    const visibleChips = React.useMemo(() => {
      if (!maxChipsDisplay || selectedOptions.length <= maxChipsDisplay) {
        return selectedOptions;
      }
      return selectedOptions.slice(0, maxChipsDisplay);
    }, [selectedOptions, maxChipsDisplay]);

    const hiddenChipsCount = selectedOptions.length - visibleChips.length;

    // Virtual / Windowed slice rendering for large lists (>50 options)
    const VIRTUAL_THRESHOLD = 50;
    const WINDOW_SIZE = 40;
    const ITEM_HEIGHT = 40;
    const isVirtual = filteredOptions.length > VIRTUAL_THRESHOLD;

    const { visibleSlice, startIndex, topSpacerHeight, bottomSpacerHeight } = React.useMemo(() => {
      if (!isVirtual) {
        return {
          visibleSlice: filteredOptions,
          startIndex: 0,
          topSpacerHeight: 0,
          bottomSpacerHeight: 0,
        };
      }
      const buffer = Math.floor(WINDOW_SIZE / 2);
      const targetStart = highlightedIndex - buffer;
      const start = Math.max(0, Math.min(targetStart, filteredOptions.length - WINDOW_SIZE));
      const end = Math.min(filteredOptions.length, start + WINDOW_SIZE);
      return {
        visibleSlice: filteredOptions.slice(start, end),
        startIndex: start,
        topSpacerHeight: start * ITEM_HEIGHT,
        bottomSpacerHeight: (filteredOptions.length - end) * ITEM_HEIGHT,
      };
    }, [filteredOptions, highlightedIndex, isVirtual]);

    const renderSearchWell = () => (
      <div className="flex items-center gap-2 px-2.5 py-2 border-b border-border/60">
        <Search className="size-3.5 shrink-0 text-muted-foreground" />
        <input
          ref={inputRef}
          type="text"
          data-testid="combobox-search-input"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={searchPlaceholder}
          className="w-full bg-transparent text-xs text-foreground placeholder:text-muted-foreground outline-none"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery("")}
            className="text-muted-foreground hover:text-foreground cursor-pointer"
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>
    );

    const renderListboxOptions = () => (
      <div
        ref={listRef}
        id={listboxId}
        role="listbox"
        aria-multiselectable={multiple}
        className="max-h-60 overflow-y-auto p-1 space-y-0.5"
      >
        {filteredOptions.length === 0 ? (
          <div className="py-6 text-center text-xs text-muted-foreground">
            {emptyText}
          </div>
        ) : (
          <>
            {topSpacerHeight > 0 && (
              <div style={{ height: topSpacerHeight }} aria-hidden="true" />
            )}
            {visibleSlice.map((opt, sliceIdx) => {
              const index = startIndex + sliceIdx;
              const isSelected = selectedValues.includes(opt.value);
              const isHighlighted = highlightedIndex === index;
              const Icon = opt.icon;
              const optionId = getOptionId(opt.value);

              return (
                <div
                  key={opt.value}
                  id={optionId}
                  role="option"
                  aria-selected={isSelected}
                  data-highlighted={isHighlighted}
                  data-testid={`combobox-option-${opt.value}`}
                  onClick={() => handleSelectOption(opt)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={cn(
                    "flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-xs transition-colors cursor-pointer select-none min-h-[44px] md:min-h-9",
                    isHighlighted && "bg-accent text-accent-foreground",
                    !isHighlighted && "text-muted-foreground hover:text-foreground",
                    isSelected && "text-foreground font-medium",
                    opt.disabled && "cursor-not-allowed opacity-40"
                  )}
                >
                  <div
                    className={cn(
                      "flex size-4 shrink-0 items-center justify-center rounded-sm border transition-colors",
                      multiple ? "rounded-sm" : "rounded-full",
                      isSelected
                        ? "bg-primary border-primary text-primary-foreground"
                        : "border-muted-foreground/40 bg-transparent"
                    )}
                  >
                    {isSelected && <Check className="size-3 stroke-[2.5]" />}
                  </div>

                  {Icon && <Icon className="size-3.5 shrink-0" />}

                  <div className="flex-1 truncate">
                    <span className="truncate">{opt.label}</span>
                    {opt.description && (
                      <span className="block text-overline-size text-muted-foreground truncate">
                        {opt.description}
                      </span>
                    )}
                  </div>

                  {opt.badge && (
                    <span className="rounded bg-muted px-1.5 py-0.5 text-overline-size text-muted-foreground">
                      {opt.badge}
                    </span>
                  )}
                </div>
              );
            })}
            {bottomSpacerHeight > 0 && (
              <div style={{ height: bottomSpacerHeight }} aria-hidden="true" />
            )}
          </>
        )}
      </div>
    );

    const renderClearAll = () =>
      multiple && selectedValues.length > 0 ? (
        <div className="flex items-center justify-between border-t border-border/60 px-2 py-1.5 text-xs text-muted-foreground">
          <span>{selectedValues.length} selected</span>
          <button
            type="button"
            data-testid="combobox-clear-all"
            onClick={() => emitChange([])}
            className="text-xs text-primary hover:underline cursor-pointer"
          >
            Clear all
          </button>
        </div>
      ) : null;

    const triggerNode = (
      <div
        ref={ref}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={activeDescendantId}
        data-testid="combobox-trigger"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={handleKeyDown}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        className={cn(
          "group relative flex min-h-11 w-full flex-wrap items-center gap-1.5 rounded-xl border border-border bg-background px-3 py-1.5 text-sm text-foreground shadow-xs transition-all cursor-pointer hover:bg-accent/40 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20",
          disabled && "cursor-not-allowed opacity-50 bg-muted/20",
          className
        )}
        {...props}
      >
        {/* Multi-Select Chips */}
        {multiple &&
          visibleChips.map((opt) => (
            <span
              key={opt.value}
              data-testid={`combobox-chip-${opt.value}`}
              className="inline-flex items-center gap-1 rounded-md bg-muted border border-border px-2 py-0.5 text-xs font-medium text-foreground"
            >
              {opt.icon && <opt.icon className="size-3 text-muted-foreground" />}
              <span className="truncate max-w-[140px]">{opt.label}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={(e) => handleRemoveChip(opt.value, e)}
                  className="ml-0.5 rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground cursor-pointer"
                >
                  <X className="size-3" />
                  <span className="sr-only">Remove {opt.label}</span>
                </button>
              )}
            </span>
          ))}

        {multiple && hiddenChipsCount > 0 && (
          <span className="inline-flex items-center rounded-md bg-muted px-1.5 py-0.5 text-xs text-muted-foreground border border-border">
            +{hiddenChipsCount} more
          </span>
        )}

        {/* Single Select Display */}
        {!multiple && selectedOptions.length > 0 && (() => {
          const SingleIcon = selectedOptions[0].icon;
          return (
            <div className="flex items-center gap-2 truncate flex-1 text-foreground">
              {SingleIcon && (
                <SingleIcon className="size-4 shrink-0 text-muted-foreground" />
              )}
              <span className="truncate">{selectedOptions[0].label}</span>
            </div>
          );
        })()}

        {/* Placeholder if empty */}
        {selectedValues.length === 0 && (
          <span className="text-muted-foreground select-none truncate">
            {placeholder}
          </span>
        )}

        <ChevronsUpDown className="ml-auto size-4 shrink-0 text-muted-foreground opacity-60 group-hover:opacity-100" />
      </div>
    );

    if (isTouch) {
      return (
        <SheetPrimitive.Root open={open} onOpenChange={setOpen}>
          <SheetPrimitive.Trigger asChild>{triggerNode}</SheetPrimitive.Trigger>
          <SheetPrimitive.Portal>
            <SheetPrimitive.Overlay className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
            <SheetPrimitive.Content
              data-testid="combobox-sheet"
              className="fixed inset-x-0 bottom-0 z-50 flex max-h-[85vh] flex-col rounded-t-modal border-t border-border bg-popover p-3 text-popover-foreground shadow-2xl focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom"
            >
              <div className="mx-auto -mt-1 mb-2 h-1.5 w-12 rounded-full bg-muted-foreground/30" />
              <SheetPrimitive.Close className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </SheetPrimitive.Close>
              <SheetPrimitive.Title className="text-sm font-semibold text-foreground px-1 mb-2">
                {placeholder || "Select option"}
              </SheetPrimitive.Title>
              <SheetPrimitive.Description className="sr-only">
                Select an option from the list
              </SheetPrimitive.Description>
              {renderSearchWell()}
              {renderListboxOptions()}
              {renderClearAll()}
            </SheetPrimitive.Content>
          </SheetPrimitive.Portal>
        </SheetPrimitive.Root>
      );
    }

    return (
      <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
        <PopoverPrimitive.Trigger asChild>{triggerNode}</PopoverPrimitive.Trigger>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            align={align}
            sideOffset={6}
            data-testid="combobox-popover"
            className="z-50 w-(--radix-popover-trigger-width) min-w-[260px] rounded-modal border border-border bg-popover p-1 text-popover-foreground shadow-xl outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          >
            {renderSearchWell()}
            {renderListboxOptions()}
            {renderClearAll()}
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    );
  }
);
Combobox.displayName = "Combobox";

const MultiSelectCombobox = React.forwardRef((props, ref) => (
  <Combobox ref={ref} multiple={true} {...props} />
));
MultiSelectCombobox.displayName = "MultiSelectCombobox";

export { Combobox, MultiSelectCombobox };
